// The six barrier grids, which differ in five ways and are otherwise one screen.
//
// | view | source | filter | group by | sort | row tap |
// | --- | --- | --- | --- | --- | --- |
// | Barriers Dashboard | open | open only | Action Category, descending | Commodity | edit |
// | HOT | open-hot | open and hot | Action Category, descending | Commodity | edit |
// | Plants | open | open only | Plant | -- | detail |
// | Department | open | open only | Owner Department, then Commodity | -- | detail |
// | Archive | archive | none | Product | -- | detail |
// | Interplant Transfers | interplant-transfers | IPT ref set | Product, then Job Number | Job Number | edit |
//
// The filters are the API's: each of the four views is a predicate written once
// in `views.py`, so a screen here names a view rather than composing a query
// nothing would keep in step with the source's.
//
// Every one of the six can also be narrowed to one Action Owner from the filter
// menu, which `ActionOwnerFilter.tsx` explains. The API does
// that narrowing too, so the groups and the count are of the matching barriers.

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataGrid, type GridColumn } from '../grid/DataGrid';
import { barrierColumns, barrierRowClass } from './barrierColumns';
import { useBarrierView, useLookups, useMe, usePerson } from '../data';
import * as api from '../api';
import type { BarrierViewName, BarrierViewRow } from '../types';
import { Screen } from '../shell/Screen';
import {
  ActionOwnerFilter,
  OWNER_PARAM,
  emptyMessage,
  ownerLabel,
  ownerParam,
  parseOwner,
  type OwnerFilter,
} from './ActionOwnerFilter';

/** The foreign keys a barrier cell may be re-pointed at inline. */
const EDITABLE_REFERENCES = [
  'site_id',
  'area_id',
  'product_id',
  'department_id',
  'commodity_id',
  'buyer_status_id',
  'warehouse_status_id',
  'root_cause_id',
  'make_parts_site_id',
];

export interface BarrierScreenProps {
  title: string;
  view: BarrierViewName;
  /** Named against the map `barrierColumns` returns, so a screen's column order
   *  reads as the list of columns it shows. */
  columns: string[];
  groupBy: string[];
  groupDesc?: boolean[];
  sortBy?: { key: string; desc?: boolean }[];
  /** The source enables quick edit on every barrier grid except `Plants`. */
  editable?: boolean;
  /** `edit` opens the row's form, `detail` opens its record. Both are the
   *  source's own behaviour per view. */
  rowTap?: 'edit' | 'detail';
  description?: string;
}

export function BarrierScreen({
  title,
  view,
  columns,
  groupBy,
  groupDesc,
  sortBy,
  editable = true,
  rowTap = 'edit',
  description,
}: BarrierScreenProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState('');

  const [params, setParams] = useSearchParams();
  const owner = parseOwner(params.get(OWNER_PARAM));
  const me = useMe();
  const person = usePerson(owner.kind === 'person' ? owner.objectId : null);
  // `undefined` holds the fetch back until `/api/me` says who "me" is.
  const actionOwner =
    owner.kind === 'everyone' ? null : owner.kind === 'me' ? me.data?.object_id : owner.objectId;
  const rows = useBarrierView(view, search, actionOwner);
  const lookups = useLookups(editable ? EDITABLE_REFERENCES : []);
  // "Me" can never be known if `/api/me` failed, so that failure is the
  // screen's rather than an endless wait.
  const meFailed = owner.kind === 'me' && me.error !== null;

  // The name the filter shows. A person no longer in the directory still
  // names barriers, which carry the name they had when they were chosen.
  const carried = (objectId: string) =>
    rows.data?.find((row) => row.action_owner_object_id?.toLowerCase() === objectId)?.action_owner;
  const myName = me.data?.display_name ?? me.data?.actor ?? 'You';
  const ownerName =
    owner.kind === 'me'
      ? myName
      : owner.kind === 'person'
        ? (person.data?.display_name ??
          person.data?.user_principal_name ??
          carried(owner.objectId) ??
          (person.loading ? '…' : owner.objectId))
        : '';

  function setOwner(next: OwnerFilter) {
    const updated = new URLSearchParams(params);
    const value = ownerParam(next);
    if (value === null) updated.delete(OWNER_PARAM);
    else updated.set(OWNER_PARAM, value);
    setParams(updated);
  }

  const ownerTag = ownerLabel(owner, ownerName);

  // Read-only until `/api/me` answers, so nobody is offered a Hot box the API
  // would refuse.
  const canSetHot = Boolean(me.data?.can_set_hot);
  const available = useMemo(
    () => barrierColumns({ editable, canSetHot, lookups: lookups.data ?? {} }),
    [editable, canSetHot, lookups.data]
  );

  const shown = useMemo<GridColumn<BarrierViewRow>[]>(
    () =>
      columns.map((name) => {
        const column = available[name];
        if (!column) throw new Error(`no barrier column named ${name}`);
        return column;
      }),
    [columns, available]
  );

  // The grouping and sort keys name grid columns, and a grid column's key is
  // the column it shows -- `Commodity` is `commodity_name`, not `commodity_id`.
  const groupKeys = groupBy.map((name) => available[name]?.key ?? name);
  const sortKeys = (sortBy ?? []).map((s) => ({ key: available[s.key]?.key ?? s.key, desc: s.desc }));

  async function edit(row: BarrierViewRow, key: string, value: unknown): Promise<void> {
    await api.updateRow('barriers', row.barrier_number, { [key]: value });
    rows.reload();
  }

  return (
    <Screen
      title={title}
      description={description}
      loading={rows.loading && !meFailed}
      error={rows.error ?? (meFailed ? me.error : null)}
      count={rows.data?.length ?? 0}
      search={{
        value: pending,
        onChange: setPending,
        // D11 closed search as a plain substring match across the key text
        // columns, done by the API. Submitting rather than typing-as-you-go,
        // because each search is a query over the whole table.
        onSubmit: () => setSearch(pending.trim()),
        onClear: () => {
          setPending('');
          setSearch('');
        },
      }}
      filters={{
        active: ownerTag ? [{ key: OWNER_PARAM, label: ownerTag, onClear: () => setOwner({ kind: 'everyone' }) }] : [],
        // Action Owner is the only filter, so clearing it is clearing them all.
        onClearAll: () => setOwner({ kind: 'everyone' }),
        children: (close) => (
          <ActionOwnerFilter
            filter={owner}
            myObjectId={me.data?.object_id ?? null}
            myName={myName}
            name={ownerName}
            onChange={(next) => {
              setOwner(next);
              close();
            }}
          />
        ),
      }}
      actions={
        <button className="primary create" onClick={() => navigate('/data/barriers/new')}>
          New barrier
        </button>
      }
    >
      <DataGrid
        rows={rows.data ?? []}
        columns={shown}
        getRowId={(row) => String(row.barrier_number)}
        groupBy={groupKeys}
        groupDesc={groupDesc}
        sortBy={sortKeys}
        rowClass={barrierRowClass}
        onEdit={editable ? edit : undefined}
        onRowClick={(row) =>
          navigate(rowTap === 'edit' ? `/data/barriers/${row.barrier_number}/edit` : `/data/barriers/${row.barrier_number}`)
        }
        actions={(row) => (
          // The source's single custom action opens a new barrier form
          // pre-populated from the selected row, bound to a `Copy` column --
          // which is why `Copy` leads several of its column orders. The column
          // is dropped here and this is the button that replaces it.
          <button
            className="link"
            onClick={() => navigate(`/data/barriers/new?duplicate=${row.barrier_number}`)}
            title="Open a new barrier pre-populated from this one"
          >
            Duplicate
          </button>
        )}
        emptyMessage={emptyMessage(search, owner, ownerName)}
      />
    </Screen>
  );
}
